import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronUp, Pencil, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AmountInput } from '../AmountInput'
import { CurrencyAmountInput } from '../CurrencyAmountInput'
import { FieldLabel } from '../FieldLabel'
import { SwitchField } from '../SwitchField'
import type { ObjectiveType } from '@/services/networks/networkFieldSettings'
import {
  MAX_PERIOD_AMOUNT,
  PER_USER_MAX_AMOUNT,
  RATE_LIMIT_PERIOD_TYPES,
  isBudgetLimit,
  newRateLimit,
  rateLimitAmount,
  rateLimitCurrency,
  rateLimitProblem,
  type RateLimit,
  type RateLimitScope,
} from '@/services/earnings/rateLimits'
import type { CappingText } from './cappingText'

/** The currency picker the parent form shares across all of its amounts. */
export interface CappingCurrencyControl {
  currency: string
  onCurrencyChange: (currency: string) => void
  currencyFallback?: string
  currencyDisabled?: boolean
}

export interface CappingRowProps {
  limit: RateLimit
  onChange: (limit: RateLimit) => void
  onRemove: () => void
  /** Fields this cap may be set on. */
  types: ObjectiveType[]
  /** Scopes this cap may be set on — one scope means the choice is not offered. */
  scopes: RateLimitScope[]
  text: CappingText
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  readOnly?: boolean
  /** Currency shown on a budget cap when the form has no picker to share. */
  currency?: string
  currencyControl?: CappingCurrencyControl
}

/** One delivery cap: a sentence describing it, with its editor a click away. */
export function CappingRow({
  limit,
  onChange,
  onRemove,
  types,
  scopes,
  text,
  expanded,
  onExpandedChange,
  readOnly,
  currency,
  currencyControl,
}: CappingRowProps) {
  const { t } = useTranslation()
  const rowId = useId()
  // Kept as typed so a half-written decimal ("1.") survives a re-render.
  const [amountText, setAmountText] = useState(() => {
    const amount = rateLimitAmount(limit)
    return amount == null ? '' : String(amount)
  })

  const problem = rateLimitProblem(limit)
  const budget = isBudgetLimit(limit)
  const capCurrency = rateLimitCurrency(limit) || currencyControl?.currency || currency

  function setAmount(next: string) {
    setAmountText(next)
    const parsed = next === '' ? null : Number(next)
    const amount = parsed == null || isNaN(parsed) ? null : parsed
    onChange({
      ...limit,
      amount:
        budget && amount != null
          ? { amount, currency: capCurrency ?? '' }
          : amount,
    })
  }

  /**
   * Switching field resets the cap the way the API needs it: a budget cap is
   * always a line item-wide daily spend, and only a budget cap carries money.
   */
  function setType(type: ObjectiveType) {
    const fresh = newRateLimit(type, limit.scope)
    const amount = rateLimitAmount(limit)
    onChange({
      ...(type === 'BUDGET' ? fresh : { ...limit, type }),
      amount:
        amount == null
          ? null
          : type === 'BUDGET'
            ? { amount, currency: capCurrency ?? '' }
            : amount,
    })
  }

  function setScope(scope: RateLimitScope) {
    // Smoothing is a line item-wide idea only.
    onChange({ ...limit, scope, smoothed: scope === 'GLOBAL' ? limit.smoothed : false })
  }

  const problemText = problem
    ? problem === 'amountTooHigh'
      ? t('lineItems.capping.errors.amountTooHigh', { max: PER_USER_MAX_AMOUNT })
      : problem === 'periodAmount'
        ? t('lineItems.capping.errors.periodAmount', { max: MAX_PERIOD_AMOUNT })
        : t('lineItems.capping.errors.amount')
    : undefined

  return (
    <div className="py-2.5 first:pt-0 last:pb-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            {!budget && scopes.length > 1 && (
              <Badge variant="gray" className="font-normal">
                {text.scope(limit.scope)}
              </Badge>
            )}
            <span>{text.describe(limit, capCurrency)}</span>
          </p>
          {problemText && <p className="mt-1 text-xs text-destructive">{problemText}</p>}
        </div>
        {!readOnly && (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={expanded ? t('common.done') : t('common.edit')}
              onClick={() => onExpandedChange(!expanded)}
            >
              {expanded ? <ChevronUp className="size-3.5" /> : <Pencil className="size-3.5" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t('common.remove')}
              onClick={onRemove}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        )}
      </div>

      {expanded && !readOnly && (
        <div className="field-grid mt-3 rounded-lg border bg-muted/30 p-3">
          <div className="p-field">
            <label>{text.typeField}</label>
            <Select label={text.typeField} value={limit.type} onValueChange={(v) => setType(v as ObjectiveType)}>
              <SelectTrigger>
                <SelectValue>{t(`lineItems.objectiveTypes.${limit.type}`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`lineItems.objectiveTypes.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="p-field">
            <FieldLabel
              hint={
                !budget && limit.scope === 'PER_USER'
                  ? t('lineItems.capping.maxHint', { max: PER_USER_MAX_AMOUNT })
                  : undefined
              }
            >
              {budget ? t('lineItems.capping.dailyLimit') : t('lineItems.capping.count')}
            </FieldLabel>
            {budget && currencyControl ? (
              <CurrencyAmountInput
                value={amountText}
                onChange={setAmount}
                min={0}
                {...currencyControl}
              />
            ) : (
              <AmountInput
                value={amountText}
                onChange={setAmount}
                unit={budget ? capCurrency : undefined}
                min={1}
                max={!budget && limit.scope === 'PER_USER' ? PER_USER_MAX_AMOUNT : undefined}
                step={budget ? 'any' : 1}
                placeholder={budget ? '0.00' : '0'}
              />
            )}
          </div>

          {!budget && scopes.length > 1 && (
            <div className="p-field full">
              <label>{text.scopeField}</label>
              <RadioGroup
                value={limit.scope}
                onValueChange={(v) => setScope(v as RateLimitScope)}
                className="gap-2"
              >
                {scopes.map((scope) => (
                  <div key={scope} className="flex items-start gap-2">
                    <RadioGroupItem value={scope} id={`${rowId}-${scope}`} className="mt-0.5" />
                    <Label htmlFor={`${rowId}-${scope}`} className="flex-col items-start gap-0.5">
                      <span className="text-sm font-medium">{text.scope(scope)}</span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {text.scopeHelp(scope)}
                      </span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {!budget && (
            <div className="p-field full">
              <label>{t('lineItems.capping.period')}</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min={1}
                  max={MAX_PERIOD_AMOUNT}
                  className="w-24"
                  value={limit.periodType === 'ALL_TIME' ? '' : limit.periodAmount}
                  disabled={limit.periodType === 'ALL_TIME'}
                  onChange={(e) =>
                    onChange({ ...limit, periodAmount: parseInt(e.target.value, 10) || 0 })
                  }
                />
                <Select
                  label={t('lineItems.capping.period')}
                  value={limit.periodType}
                  onValueChange={(v) =>
                    onChange({ ...limit, periodType: v as RateLimit['periodType'] })
                  }
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue>
                      {t(`lineItems.capping.periodTypes.${limit.periodType}`)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {RATE_LIMIT_PERIOD_TYPES.map((period) => (
                      <SelectItem key={period} value={period}>
                        {t(`lineItems.capping.periodTypes.${period}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {!budget && limit.scope === 'GLOBAL' && (
            <div className="full">
              <SwitchField
                label={text.smoothedLabel}
                tooltip={text.smoothedHelp}
                checked={limit.smoothed}
                onCheckedChange={(smoothed) => onChange({ ...limit, smoothed })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
